import type {
  BatchPricingResult,
  ClothingFilters,
  ClothingItem,
  ClothingList,
  ClothingMeta,
  MannequinList,
  OverlayFilters,
  OverlayList,
  PriceType,
  PricingItem,
  StatsCoverage,
  StatsOverview,
  StudioImage,
} from '@/api'

const PRICE_TYPES = [
  'money',
  'black_money',
  'coin',
  'ticket',
  'hidden',
] as const satisfies readonly PriceType[]

const AUTH_KEY = 'cdn-mock-auth'
const delay = (ms = 180) => new Promise((resolve) => setTimeout(resolve, ms))

const COLLECTIONS = [
  'base',
  'mpbeach',
  'mpchristmas',
  'mpheist',
  'mpgunrunning',
  'mpvinewood',
  'custom_tgn',
] as const

const GENDERS = [0, 1, 2] as const
const COMPONENT_TYPES = ['component', 'prop'] as const

/** Include 11 so Component ID filter can be exercised in mock (tops/jackets). */
const COMPONENT_IDS: Record<(typeof COMPONENT_TYPES)[number], number[]> = {
  component: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  prop: [0, 1, 2, 6, 7],
}

function svgPreview(item: {
  hash: string
  collectionName: string
  gender: number
  componentType: string
  componentId: number
}): string {
  const hue = [...item.hash].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % 360
  const gender =
    item.gender === 0 ? 'Nam' : item.gender === 1 ? 'Nữ' : 'Unisex'
  const label = `${item.collectionName}\n${gender} · ${item.componentType} ${item.componentId}`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="hsl(${hue} 42% 28%)"/>
      <stop offset="100%" stop-color="hsl(${(hue + 40) % 360} 35% 16%)"/>
    </linearGradient>
  </defs>
  <rect width="400" height="400" fill="url(#g)"/>
  <circle cx="200" cy="150" r="64" fill="hsla(0,0%,100%,0.12)"/>
  <text x="200" y="250" fill="white" font-family="ui-monospace,monospace" font-size="14" text-anchor="middle" opacity="0.9">${label
    .split('\n')
    .map(
      (line, index) =>
        `<tspan x="200" dy="${index === 0 ? 0 : 20}">${line}</tspan>`
    )
    .join('')}</text>
</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

function buildSeedItems(): ClothingItem[] {
  const items: ClothingItem[] = []
  let n = 0
  for (const collectionName of COLLECTIONS) {
    for (const gender of GENDERS) {
      for (const componentType of COMPONENT_TYPES) {
        for (const componentId of COMPONENT_IDS[componentType]) {
          for (let drawableId = 0; drawableId < 3; drawableId++) {
            for (let textureId = 0; textureId < 2; textureId++) {
              n += 1
              const genderKey =
                gender === 0 ? 'male' : gender === 1 ? 'female' : 'unisex'
              const typeKey = componentType === 'component' ? 'c' : 'p'
              const hash = `${genderKey}_${typeKey}_${componentId}_${collectionName}_${drawableId}_${textureId}`
              const type = PRICE_TYPES[n % PRICE_TYPES.length]
              const unpriced = n % 7 === 0
              const item: ClothingItem = {
                hash,
                collectionName,
                gender,
                componentType,
                componentId,
                drawableId,
                textureId,
                price: unpriced ? 0 : 50 + (n % 40) * 25,
                type: unpriced ? 'hidden' : type,
                size: 12_000 + (n % 80) * 512,
                fileName: `${collectionName}-${gender}-${componentType}-${componentId}-${drawableId}-${textureId}.webp`,
                previewUrl: '',
              }
              item.previewUrl = svgPreview(item)
              items.push(item)
            }
          }
        }
      }
    }
  }
  return items
}

let items = buildSeedItems()

/** Số kiểu của từng head overlay trong GTA (id 0..12). */
const OVERLAY_STYLE_COUNTS = [24, 29, 34, 15, 75, 7, 12, 11, 10, 18, 17, 12, 2]

function studioSvgPreview(title: string, subtitle: string, hue: number) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400">
  <ellipse cx="200" cy="170" rx="90" ry="112" fill="hsl(${hue} 10% 62%)"/>
  <rect x="120" y="260" width="160" height="110" rx="40" fill="hsl(${hue} 10% 52%)"/>
  <text x="200" y="176" fill="hsl(${hue} 45% 22%)" font-family="ui-monospace,monospace" font-size="20" text-anchor="middle">${title}</text>
  <text x="200" y="320" fill="white" font-family="ui-monospace,monospace" font-size="15" text-anchor="middle" opacity="0.85">${subtitle}</text>
</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

function buildStudioImages(): StudioImage[] {
  const list: StudioImage[] = []
  let n = 0
  for (const gender of [0, 1]) {
    OVERLAY_STYLE_COUNTS.forEach((count, overlayId) => {
      for (let style = 0; style < count; style++) {
        n += 1
        const fileName = `base-${gender}-overlay-${overlayId}-${style}-0.webp`
        list.push({
          fileName,
          kind: 'overlay',
          collectionName: 'base',
          gender,
          overlayId,
          style,
          size: 9_000 + (n % 50) * 256,
          modifiedAt: 1_760_000_000 + n * 60,
          previewUrl: studioSvgPreview(
            `#${overlayId}`,
            `${gender === 0 ? 'Nam' : 'Nữ'} · kiểu ${style}`,
            (overlayId * 27) % 360
          ),
        })
      }
    })
  }
  // Thiếu ảnh thân nữ để xem trạng thái "chưa chụp".
  for (const [gender, shot] of [
    [0, 'face'],
    [0, 'body'],
    [1, 'face'],
  ] as const) {
    list.push({
      fileName: `base-${gender}-mannequin-${shot}-0-0.webp`,
      kind: 'mannequin',
      collectionName: 'base',
      gender,
      overlayId: 0,
      style: 0,
      shot,
      size: 18_000 + gender * 1024,
      modifiedAt: 1_760_000_000,
      previewUrl: studioSvgPreview(
        shot === 'face' ? 'MẶT' : 'THÂN',
        gender === 0 ? 'Nam' : 'Nữ',
        200
      ),
    })
  }
  return list
}

let studioImages = buildStudioImages()

function isAuthenticated() {
  return sessionStorage.getItem(AUTH_KEY) === '1'
}

function setAuthenticated(value: boolean) {
  if (value) sessionStorage.setItem(AUTH_KEY, '1')
  else sessionStorage.removeItem(AUTH_KEY)
}

function matchesFilters(item: ClothingItem, filters: ClothingFilters) {
  if (filters.collection && item.collectionName !== filters.collection)
    return false
  if (filters.gender !== undefined && filters.gender !== '') {
    if (String(item.gender) !== String(filters.gender)) return false
  }
  if (filters.componentType && item.componentType !== filters.componentType)
    return false
  if (
    filters.componentId !== undefined &&
    filters.componentId !== '' &&
    String(item.componentId) !== String(filters.componentId)
  ) {
    return false
  }
  if (
    filters.drawableId !== undefined &&
    filters.drawableId !== '' &&
    String(item.drawableId) !== String(filters.drawableId)
  ) {
    return false
  }
  if (
    filters.textureId !== undefined &&
    filters.textureId !== '' &&
    String(item.textureId) !== String(filters.textureId)
  ) {
    return false
  }
  if (filters.hash && !item.hash.toLowerCase().includes(filters.hash.toLowerCase()))
    return false
  if (filters.type && item.type !== filters.type) return false
  if (
    filters.priceMin !== undefined &&
    filters.priceMin !== '' &&
    item.price < Number(filters.priceMin)
  ) {
    return false
  }
  if (
    filters.priceMax !== undefined &&
    filters.priceMax !== '' &&
    item.price > Number(filters.priceMax)
  ) {
    return false
  }
  return true
}

function sortItems(list: ClothingItem[], sort = 'collection') {
  const next = [...list]
  next.sort((a, b) => {
    switch (sort) {
      case 'price':
        return a.price - b.price
      case 'price_desc':
        return b.price - a.price
      case 'hash':
        return a.hash.localeCompare(b.hash)
      case 'collection':
      default:
        return (
          a.collectionName.localeCompare(b.collectionName) ||
          a.hash.localeCompare(b.hash)
        )
    }
  })
  return next
}

function overviewFrom(list: ClothingItem[]): StatsOverview {
  const byGender: Record<string, number> = {}
  const byType: Record<string, number> = {}
  const byComponentType: Record<string, number> = {}
  let priced = 0
  for (const item of list) {
    byGender[String(item.gender)] = (byGender[String(item.gender)] ?? 0) + 1
    byType[item.type] = (byType[item.type] ?? 0) + 1
    byComponentType[item.componentType] =
      (byComponentType[item.componentType] ?? 0) + 1
    if (item.price > 0 && item.type !== 'hidden') priced += 1
  }
  return {
    total: list.length,
    priced,
    unpriced: list.length - priced,
    byGender,
    byType,
    byComponentType,
  }
}

function coverageFrom(list: ClothingItem[]): StatsCoverage {
  const map = new Map<
    string,
    { name: string; total: number; priced: number; unpriced: number }
  >()
  for (const item of list) {
    const row = map.get(item.collectionName) ?? {
      name: item.collectionName,
      total: 0,
      priced: 0,
      unpriced: 0,
    }
    row.total += 1
    if (item.price > 0 && item.type !== 'hidden') row.priced += 1
    else row.unpriced += 1
    map.set(item.collectionName, row)
  }
  return {
    collections: [...map.values()]
      .map((row) => ({
        ...row,
        pricedPercent: row.total === 0 ? 0 : (row.priced / row.total) * 100,
      }))
      .sort((a, b) => b.unpriced - a.unpriced || a.name.localeCompare(b.name)),
  }
}

export const mockApi = {
  async login(_username: string, _password: string) {
    await delay()
    setAuthenticated(true)
    return { authenticated: true }
  },
  async logout() {
    await delay(80)
    setAuthenticated(false)
    return { authenticated: false }
  },
  async me() {
    await delay(60)
    return { authenticated: isAuthenticated() }
  },
  async listClothing(filters: ClothingFilters = {}): Promise<ClothingList> {
    await delay()
    const page = Math.max(1, Number(filters.page ?? 1))
    const perPage = Math.max(1, Number(filters.perPage ?? 48))
    const filtered = sortItems(
      items.filter((item) => matchesFilters(item, filters)),
      filters.sort
    )
    const total = filtered.length
    const totalPages = Math.max(1, Math.ceil(total / perPage))
    const start = (page - 1) * perPage
    return {
      items: filtered.slice(start, start + perPage),
      total,
      page,
      perPage,
      totalPages,
    }
  },
  async clothingMeta(): Promise<ClothingMeta> {
    await delay(80)
    return {
      collections: [...new Set(items.map((item) => item.collectionName))].sort(),
      componentTypes: [...COMPONENT_TYPES],
      types: [...PRICE_TYPES],
      total: items.length,
    }
  },
  async getClothing(hash: string) {
    await delay(80)
    const item = items.find((row) => row.hash === hash)
    if (!item) throw new Error('Asset not found')
    return item
  },
  async patchClothing(
    hash: string,
    body: Partial<Pick<ClothingItem, 'price' | 'type'>>
  ) {
    await delay()
    const index = items.findIndex((row) => row.hash === hash)
    if (index < 0) throw new Error('Asset not found')
    const next = {
      ...items[index],
      ...(body.price !== undefined ? { price: body.price } : {}),
      ...(body.type !== undefined ? { type: body.type as PriceType } : {}),
    }
    items = items.map((row, i) => (i === index ? next : row))
    return next
  },
  async batchPricing(payload: PricingItem[]): Promise<BatchPricingResult> {
    await delay(220)
    const failed: BatchPricingResult['failed'] = []
    let updated = 0
    const lookup = new Map(payload.map((row) => [row.hash, row]))
    items = items.map((item) => {
      const patch = lookup.get(item.hash)
      if (!patch) return item
      if (patch.price < 0) {
        failed.push({ hash: item.hash, error: 'Giá không hợp lệ' })
        return item
      }
      updated += 1
      return { ...item, price: patch.price, type: patch.type }
    })
    for (const row of payload) {
      if (!items.some((item) => item.hash === row.hash)) {
        failed.push({ hash: row.hash, error: 'Asset not found' })
      }
    }
    return { updated, failed }
  },
  async deleteClothing(hash: string) {
    await delay()
    const before = items.length
    items = items.filter((item) => item.hash !== hash)
    if (items.length === before) throw new Error('Asset not found')
    return { success: true }
  },
  async statsOverview() {
    await delay()
    return overviewFrom(items)
  },
  async statsCoverage() {
    await delay()
    return coverageFrom(items)
  },
  async flushClothingCache() {
    await delay(100)
    return { success: true }
  },
  async listOverlays(filters: OverlayFilters = {}): Promise<OverlayList> {
    await delay()
    const overlays = studioImages.filter((image) => image.kind === 'overlay')
    const byGender = overlays.filter(
      (image) => !filters.gender || String(image.gender) === filters.gender
    )
    const counts: Record<string, number> = {}
    for (const image of byGender) {
      counts[String(image.overlayId)] =
        (counts[String(image.overlayId)] ?? 0) + 1
    }
    const filtered = byGender.filter(
      (image) =>
        !filters.overlayId || String(image.overlayId) === filters.overlayId
    )
    const perPage = Math.max(1, Number(filters.perPage ?? 48))
    const total = filtered.length
    const totalPages = Math.max(1, Math.ceil(total / perPage))
    const page = Math.min(Math.max(1, Number(filters.page ?? 1)), totalPages)
    const start = (page - 1) * perPage
    return {
      items: filtered.slice(start, start + perPage),
      total,
      overall: overlays.length,
      page,
      perPage,
      totalPages,
      counts,
    }
  },
  async listMannequins(): Promise<MannequinList> {
    await delay()
    return {
      items: studioImages.filter((image) => image.kind === 'mannequin'),
    }
  },
  async deleteStudioImage(fileName: string) {
    await delay()
    const before = studioImages.length
    studioImages = studioImages.filter((image) => image.fileName !== fileName)
    if (studioImages.length === before) throw new Error('image not found')
    return { success: true }
  },
}

export const isMockApiEnabled = import.meta.env.VITE_USE_MOCK === 'true'
